<template>
  <div class="grid">
    <div class="col-12 mb-3">
      <span class="p-float-label">
        <InputText id="name" v-model="commodity.name" class="w-full" />
        <label for="name">Name*</label>
      </span>
      <small v-if="submitted && !commodity.name" class="p-error">Name is required.</small>
    </div>

    <div class="col-12 mb-3">
      <span class="p-float-label">
        <Select
          id="category"
          v-model="commodity.category"
          :options="categories"
          class="w-full"
          placeholder="Select a category"
        />
        <label for="category">Category*</label>
      </span>
      <small v-if="submitted && !commodity.category" class="p-error">Category is required.</small>
    </div>

    <div class="col-12 mb-3">
      <span class="p-float-label">
        <InputNumber
          id="price"
          v-model="commodity.current_price_per_lb"
          mode="currency"
          currency="USD"
          locale="en-US"
          :minFractionDigits="2"
          class="w-full"
        />
        <label for="price">Price per lb ($)*</label>
      </span>
      <small v-if="submitted && !commodity.current_price_per_lb" class="p-error">
        Price is required.
      </small>
    </div>

    <div class="col-12 mb-3">
      <span class="p-float-label">
        <Textarea id="description" v-model="commodity.description" rows="3" class="w-full" />
        <label for="description">Description</label>
      </span>
    </div>

    <div class="col-12 mb-3">
      <div class="flex align-items-center">
        <ToggleSwitch v-model="commodity.is_active" />
        <label for="is_active" class="ml-2">Active</label>
      </div>
    </div>

    <div class="col-12 mb-3">
      <div class="flex align-items-center">
        <ToggleSwitch v-model="commodity.is_favorite" />
        <label for="is_favorite" class="ml-2">Favorite</label>
      </div>
    </div>

    <div class="col-12" v-if="commodity.id">
      <div class="surface-100 p-3 border-round">
        <div class="text-500 mb-2">Last Updated: {{ formatDate(commodity.updated_at) }}</div>
        <div class="text-500">Created: {{ formatDate(commodity.created_at) }}</div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, watch, computed } from 'vue'
import type { CommodityType } from '@/types/CommodityType'
import { useSettingsStore } from '@/stores/settings'
import InputText from 'primevue/inputtext'
import InputNumber from 'primevue/inputnumber'
import Select from 'primevue/select'
import Textarea from 'primevue/textarea'
import ToggleSwitch from 'primevue/toggleswitch'

const props = defineProps({
  initialCommodity: {
    type: Object as () => CommodityType,
    required: true,
  },
  submitted: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['update:commodity'])

const settingsStore = useSettingsStore()

// Categories for dropdown
const categories = computed(() => settingsStore.commoditySettings.categories)

// Create a reactive copy of the commodity
const commodity = reactive<CommodityType>({ ...props.initialCommodity })

// Watch for changes and emit updates
watch(
  commodity,
  (newValue) => {
    emit('update:commodity', { ...newValue })
  },
  { deep: true },
)

// Watch for changes in initialCommodity prop
watch(
  () => props.initialCommodity,
  (newValue) => {
    Object.assign(commodity, newValue)
  },
  { deep: true },
)

function formatDate(timestamp: any): string {
  if (!timestamp) return 'N/A'
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp)
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date)
}
</script>
